import React, { useState, useEffect } from "react";
import type {
  DestinationFormData,
  DestinationFormErrors,
  DestinationFormProps,
} from "../../types/destination-types";
import {
  DEFAULT_DESTINATION_FORM_DATA,
  DESTINATION_CATEGORIES,
  validateDestinationForm,
  isDestinationFormValid,
  formatDestinationUrl,
} from "../../types/destination-types";

export function DestinationForm({
  destination,
  onSubmit,
  onCancel,
  loading = false,
  mode,
}: DestinationFormProps) {
  const [formData, setFormData] = useState<DestinationFormData>(() => {
    if (destination && mode === "edit") {
      return {
        name: destination.name,
        url: destination.url,
        category: destination.category,
        description: destination.description || "",
        tags: [...destination.tags],
        trackingEnabled: destination.trackingEnabled,
        file: undefined,
        fileName: (destination as any).fileName || "",
      };
    }
    return { ...DEFAULT_DESTINATION_FORM_DATA };
  });

  const [errors, setErrors] = useState<DestinationFormErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validate form whenever data changes
  useEffect(() => {
    if (isDirty) {
      const validationErrors = validateDestinationForm(formData);
      setErrors(validationErrors);
    }
  }, [formData, isDirty]);

  const handleInputChange = (
    field: keyof DestinationFormData,
    value: string | boolean | string[]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleTagAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const tag = tagInput.trim();
      if (tag && !formData.tags.includes(tag) && formData.tags.length < 10) {
        handleInputChange("tags", [...formData.tags, tag]);
        setTagInput("");
      }
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    handleInputChange(
      "tags",
      formData.tags.filter((tag) => tag !== tagToRemove)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission
    if (loading || isSubmitting) {
      return;
    }

    setIsDirty(true);
    setIsSubmitting(true);

    const validationErrors = validateDestinationForm(formData);
    setErrors(validationErrors);

    if (!isDestinationFormValid(validationErrors)) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Format URL before submitting (except for FILE category)
      const submitData = {
        ...formData,
        url:
          formData.category === "FILE"
            ? ""
            : formatDestinationUrl(formData.url),
      };
      await onSubmit(submitData);
    } catch (error) {
      console.error("Form submission error:", error);
      setErrors({ general: "Failed to save destination. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = isDestinationFormValid(errors);
  const canSubmit = isFormValid && isDirty && !loading && !isSubmitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{errors.general}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Destination Name */}
        <div>
          <label
            htmlFor="dest-name"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Destination Name *
          </label>
          <input
            type="text"
            id="dest-name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.name ? "border-red-300" : "border-gray-300"
            }`}
            placeholder="e.g., Product Demo Page, Contact Form"
            disabled={loading || isSubmitting}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label
            htmlFor="dest-category"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Category *
          </label>
          <select
            id="dest-category"
            value={formData.category}
            onChange={(e) =>
              handleInputChange("category", e.target.value as any)
            }
            className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.category ? "border-red-300" : "border-gray-300"
            }`}
            disabled={loading || isSubmitting}
          >
            {DESTINATION_CATEGORIES.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
          {errors.category && (
            <p className="mt-1 text-sm text-red-600">{errors.category}</p>
          )}
        </div>
      </div>

      {/* Destination URL or File Upload */}
      {formData.category === "FILE" ? (
        <div>
          <label
            htmlFor="dest-file"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Upload File *
          </label>
          <input
            type="file"
            id="dest-file"
            accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleInputChange("file" as any, file as any);
                handleInputChange("fileName" as any, file.name as any);
              }
            }}
            className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.file ? "border-red-300" : "border-gray-300"
            }`}
            disabled={loading || isSubmitting}
          />
          {errors.file && (
            <p className="mt-1 text-sm text-red-600">{errors.file}</p>
          )}
          {formData.fileName && (
            <p className="mt-1 text-sm text-green-600">
              Selected: {formData.fileName}
            </p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Upload a file that users can download directly. Supported formats:
            PDF, DOC, DOCX, TXT, PNG, JPG, JPEG (max 10MB).
          </p>
        </div>
      ) : (
        <div>
          <label
            htmlFor="dest-url"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Destination URL *
          </label>
          <input
            type="url"
            id="dest-url"
            value={formData.url}
            onChange={(e) => handleInputChange("url", e.target.value)}
            className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.url ? "border-red-300" : "border-gray-300"
            }`}
            placeholder="https://example.com/destination"
            disabled={loading || isSubmitting}
          />
          {errors.url && (
            <p className="mt-1 text-sm text-red-600">{errors.url}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            The URL where users will be redirected when they click the
            destination link.
          </p>
        </div>
      )}

      {/* Description */}
      <div>
        <label
          htmlFor="dest-description"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Description
        </label>
        <textarea
          id="dest-description"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.description ? "border-red-300" : "border-gray-300"
          }`}
          rows={3}
          placeholder="Brief description of this destination and its purpose"
          disabled={loading || isSubmitting}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
        <p className="mt-1 text-sm text-gray-500">
          {formData.description.length}/500 characters
        </p>
      </div>

      {/* Tags */}
      {/* <div>
        <label
          htmlFor="dest-tags"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Tags
        </label>
        <div className="space-y-2">
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleTagRemove(tag)}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none focus:bg-blue-200 focus:text-blue-600"
                    disabled={loading || isSubmitting}
                  >
                    <svg
                      className="w-2 h-2"
                      fill="currentColor"
                      viewBox="0 0 8 8"
                    >
                      <path d="M1.41 0l-1.41 1.41.72.72 1.78 1.81-1.78 1.78-.72.69 1.41 1.44.72-.72 1.81-1.81 1.78 1.81.69.72 1.44-1.44-.72-.69-1.81-1.78 1.81-1.81.72-.72-1.44-1.41-.69.72-1.78 1.78-1.81-1.78-.72-.72z" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            type="text"
            id="dest-tags"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagAdd}
            className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.tags ? "border-red-300" : "border-gray-300"
            }`}
            placeholder="Type a tag and press Enter or comma to add"
            disabled={loading || isSubmitting || formData.tags.length >= 10}
          />
          {errors.tags && (
            <p className="mt-1 text-sm text-red-600">{errors.tags}</p>
          )}
          <p className="text-sm text-gray-500">
            {formData.tags.length}/10 tags. Press Enter or comma to add tags.
          </p>
        </div>
      </div> */}

      {/* Tracking Enabled */}
      {/* <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="track-conversions"
          checked={formData.trackingEnabled}
          onChange={(e) =>
            handleInputChange("trackingEnabled", e.target.checked)
          }
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          disabled={loading || isSubmitting}
        />
        <label htmlFor="track-conversions" className="text-sm text-gray-700">
          Enable click tracking and analytics
        </label>
      </div> */}

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={loading || isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className={`px-4 py-2 rounded-lg text-white transition-colors ${
            canSubmit
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          {loading || isSubmitting ? (
            <div className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {mode === "create" ? "Creating..." : "Updating..."}
            </div>
          ) : mode === "create" ? (
            "Create Destination"
          ) : (
            "Update Destination"
          )}
        </button>
      </div>
    </form>
  );
}
